/**
 * Runtime loader for the live performance data.
 *
 * The whole Performance page is driven by a single external file:
 *
 *     public/data/performance.json   ->   /data/performance.json  (after build)
 *
 * That file is copied to the site as-is (it is NOT bundled into the app), so
 * refreshing the numbers later only means replacing `performance.json` on the
 * server — no rebuild required. Everything else (headline stats, the equity /
 * drawdown curves, the monthly table, the recent-trade list) is derived from
 * it at runtime in `src/utils/performanceData.ts`.
 */

/** A single closed trade: ['YYYY.MM.DD', profitInUSC]. */
export type TradeEntry = [string, number];

export interface AccountMeta {
  accountNumber: string;
  broker: string;
  platform: string;
  accountType: string;
  currency: string;
  startingDeposit: number;
  withdrawal: number;
  openPositions: number;
  verification: string;
  maxDrawdown: string;
  sharpeRatio: string;
  recoveryFactor: string;
  avgHoldTime: string;
  longShortSplit: string;
}

export interface PerformanceSource {
  startingBalance: number;
  accountMeta: AccountMeta;
  trades: TradeEntry[];
}

/** Absolute URL of the external data file, honouring the deploy base path. */
export function getPerformanceDataUrl(): string {
  const base = import.meta.env.BASE_URL || '/';
  return `${base.replace(/\/$/, '')}/data/performance.json`;
}

const DATE_PATTERN = /^\d{4}\.\d{2}\.\d{2}$/;

function isTradeEntry(value: unknown): value is TradeEntry {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    typeof value[0] === 'string' &&
    DATE_PATTERN.test(value[0]) &&
    typeof value[1] === 'number' &&
    Number.isFinite(value[1])
  );
}

/** Validate the parsed JSON and fail with a human-readable reason. */
function validateSource(raw: unknown): PerformanceSource {
  if (!raw || typeof raw !== 'object') {
    throw new Error('The data file is empty or not a valid JSON object.');
  }

  const candidate = raw as Partial<PerformanceSource>;

  if (typeof candidate.startingBalance !== 'number' || !Number.isFinite(candidate.startingBalance)) {
    throw new Error('The data file is missing a numeric "startingBalance".');
  }

  if (!candidate.accountMeta || typeof candidate.accountMeta !== 'object') {
    throw new Error('The data file is missing the "accountMeta" object.');
  }

  if (!Array.isArray(candidate.trades)) {
    throw new Error('The data file is missing the "trades" array.');
  }

  const badIndex = candidate.trades.findIndex((entry) => !isTradeEntry(entry));
  if (badIndex !== -1) {
    throw new Error(
      `Trade #${badIndex + 1} in the data file is malformed. Each trade must look like ["2026.01.28", 112.7].`,
    );
  }

  return {
    startingBalance: candidate.startingBalance,
    accountMeta: candidate.accountMeta as AccountMeta,
    trades: candidate.trades as TradeEntry[],
  };
}

/**
 * Fetch and validate the external performance data. A cache-busting query is
 * appended so that replacing the file is picked up on the next page load.
 */
export async function loadPerformanceSource(): Promise<PerformanceSource> {
  const url = `${getPerformanceDataUrl()}?_=${Date.now()}`;

  let response: Response;
  try {
    response = await fetch(url, { cache: 'no-store' });
  } catch {
    throw new Error('Could not reach the trading data file. Check your connection and try again.');
  }

  if (!response.ok) {
    throw new Error(`The trading data file could not be loaded (HTTP ${response.status}).`);
  }

  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    throw new Error('The trading data file is not valid JSON. Check performance.json for a typo.');
  }

  return validateSource(parsed);
}